import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setLanguage } from '../store/languageSlice';
import logo from '../assets/ized.jpg';

const TAGLINE = {
  en: 'Platform for schools and training centres. Sign in to manage your organisation on Ized Pro.',
  fr: 'Plateforme pour écoles et centres de formation. Connectez-vous pour gérer votre organisation sur Ized Pro.',
  ar: 'منصة للمدارس ومراكز التكوين. سجّل الدخول لإدارة مؤسستك عبر Ized Pro.',
};

/**
 * Full document navigation to /app/login — required because this CRA app’s React Router
 * would otherwise treat /app/* as in-app routes and keep showing the landing shell.
 */
function IzedLandingPage() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const language = useSelector((state) => state.language.language);

  const setLang = (lang) => {
    dispatch(setLanguage(lang));
    i18n.changeLanguage(lang);
  };

  const tag =
    TAGLINE[i18n.language] || TAGLINE[i18n.language?.split('-')[0]] || TAGLINE.en;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#0d1117',
        color: '#f0f6fc',
      }}
    >
      <Box
        component="header"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: { xs: 2, sm: 3 },
          py: 2,
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Box component="img" src={logo} alt="Ized Pro" sx={{ height: 44, borderRadius: 1 }} />
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {['en', 'fr', 'ar'].map((lang) => (
            <Button
              key={lang}
              size="small"
              onClick={() => setLang(lang)}
              variant={language === lang ? 'contained' : 'text'}
              color="inherit"
              sx={{
                minWidth: 44,
                color: language === lang ? '#0d1117' : 'inherit',
                bgcolor: language === lang ? '#239e9b' : 'transparent',
              }}
            >
              {lang.toUpperCase()}
            </Button>
          ))}
          <Button
            href="/app/login"
            component="a"
            variant="contained"
            sx={{ bgcolor: '#239e9b', '&:hover': { bgcolor: '#1d827f' } }}
          >
            {t('login')}
          </Button>
        </Stack>
      </Box>

      <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 8 }}>
        <Typography
          variant="h2"
          component="h1"
          sx={{ fontWeight: 800, letterSpacing: '-0.03em', mb: 2, textAlign: 'center' }}
        >
          Ized Pro
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.88, mb: 4, textAlign: 'center', lineHeight: 1.6 }}>
          {tag}
        </Typography>
        <Stack spacing={2} alignItems="center">
          <Button
            href="/app/login"
            component="a"
            variant="contained"
            size="large"
            sx={{ px: 4, bgcolor: '#239e9b', '&:hover': { bgcolor: '#1d827f' } }}
          >
            {t('login')}
          </Button>
        </Stack>
      </Container>

      <Box
        component="footer"
        sx={{
          py: 2,
          textAlign: 'center',
          borderTop: '1px solid rgba(255,255,255,0.08)',
          typography: 'body2',
          opacity: 0.65,
        }}
      >
        © {new Date().getFullYear()} Ized Pro ·{' '}
        <Box component="a" href="https://izedpro.net" sx={{ color: '#239e9b' }}>
          izedpro.net
        </Box>
      </Box>
    </Box>
  );
}

export default IzedLandingPage;
